import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { publicDocumentRequests } from '../services/api';

const REQUEST_TYPES = [
  { v: 'form_b', label: 'Form B', desc: 'Information Certificate' },
  { v: 'form_f', label: 'Form F', desc: 'Certificate of Payment' },
  { v: 'specific_documents', label: 'Specific Documents', desc: 'Bylaws, minutes, etc.' },
];

const ROLES = ['realtor', 'lawyer', 'notary', 'other'];

export default function DocumentRequestPortal() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [building, setBuilding] = useState(null);
  const [unitList, setUnitList] = useState([]);
  const [unitId, setUnitId] = useState('');
  const [requestType, setRequestType] = useState('form_b');
  const [notes, setNotes] = useState('');
  const [form, setForm] = useState({
    requester_name: '', requester_email: '', requester_phone: '',
    requester_company: '', requester_role: 'realtor',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2 || building) { setResults([]); return; }
    const t = setTimeout(() => {
      publicDocumentRequests.searchBuildings(query).then((res) => setResults(res.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query, building]);

  useEffect(() => {
    setUnitId('');
    setUnitList([]);
    if (building && requestType !== 'specific_documents') {
      publicDocumentRequests.searchUnits(building.id).then((res) => setUnitList(res.data)).catch(() => {});
    }
  }, [building, requestType]);

  const selectBuilding = (b) => { setBuilding(b); setQuery(b.name); setResults([]); };
  const changeBuilding = () => { setBuilding(null); setQuery(''); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = building && form.requester_name.trim() && form.requester_email.trim() &&
    (requestType === 'specific_documents' || unitId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error('Please fill in the required fields'); return; }
    setSubmitting(true);
    try {
      const created = await publicDocumentRequests.create({
        building_id: building.id,
        unit_id: unitId || null,
        request_type: requestType,
        notes: requestType === 'specific_documents' ? notes : undefined,
        ...form,
      });
      const checkout = await publicDocumentRequests.checkout(created.data.id, created.data.access_token);
      window.location.href = checkout.data.url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit request');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Request Strata Documents</h1>
          <p className="text-gray-500 text-sm mt-1">Form B, Form F, or specific documents — for realtors, lawyers, and notaries.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
          {/* Building search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Building</label>
            {building ? (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-gray-900">{building.name}</p>
                  <p className="text-xs text-gray-500">{building.address}, {building.city}</p>
                </div>
                <button type="button" onClick={changeBuilding} className="text-xs text-blue-600 hover:underline shrink-0">Change</button>
              </div>
            ) : (
              <div className="relative">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by building name, address, or strata plan…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {results.map((b) => (
                      <button key={b.id} type="button" onClick={() => selectBuilding(b)}
                        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b last:border-b-0 border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{b.name}</p>
                        <p className="text-xs text-gray-500">{b.address}, {b.city}{b.strata_plan ? ` · ${b.strata_plan}` : ''}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Request type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What do you need?</label>
            <div className="grid grid-cols-3 gap-2">
              {REQUEST_TYPES.map((t) => (
                <button key={t.v} type="button" onClick={() => setRequestType(t.v)}
                  className={`text-left p-2.5 rounded-lg border-2 transition-all text-xs ${
                    requestType === t.v ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="font-medium text-gray-800 block">{t.label}</span>
                  <span className="text-gray-500">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {requestType !== 'specific_documents' && building && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Unit</label>
              <select value={unitId} onChange={(e) => setUnitId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select a unit…</option>
                {unitList.map((u) => <option key={u.id} value={u.id}>Unit {u.unit_number}</option>)}
              </select>
            </div>
          )}

          {requestType === 'specific_documents' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">What documents do you need?</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                placeholder="e.g. Current bylaws and the last two AGM minutes"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
          )}

          <hr className="border-gray-100" />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Your name</label>
              <input value={form.requester_name} onChange={set('requester_name')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input type="email" value={form.requester_email} onChange={set('requester_email')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
              <input value={form.requester_phone} onChange={set('requester_phone')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
              <input value={form.requester_company} onChange={set('requester_company')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">I am a…</label>
            <select value={form.requester_role} onChange={set('requester_role')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {ROLES.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>

          <button type="submit" disabled={!canSubmit || submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg text-sm disabled:opacity-60 transition-colors">
            {submitting ? 'Redirecting to payment…' : 'Continue to Payment'}
          </button>
          <p className="text-xs text-gray-400 text-center">
            You'll be redirected to Stripe to pay the document fee. Once approved by the strata manager,
            you'll receive a secure link to download your document.
          </p>
        </form>
      </div>
    </div>
  );
}
